import api from "../../../shared/api.jsx";

/**
 * Orders Repository - Data access layer for orders
 */

const ORDERS_ENDPOINTS = {
  base: "/orders",
  operationalSummary: "/orders/operational-summary",
  createFromQuote: "/orders/create-from-quote",
  byId: (id) => `/orders/${id}`,
  completionEligibility: (id) => `/orders/${id}/completion-eligibility`,
  status: (id) => `/orders/${id}/status`,
  assign: (id) => `/orders/${id}/assign`,
  statusTemplates: (orderId, status) => `/orders/${orderId}/status-templates/${status}`,
  statusCommunications: (orderId) => `/orders/${orderId}/status-communications`,
  statusCommunication: (orderId, status) => `/orders/${orderId}/status-communications/${status}`,
  participants: (orderId) => `/orders/${orderId}/participants`,
  invoiceLinkCandidates: (orderId) => `/orders/${orderId}/invoice-link-candidates`,
  linkInvoice: (orderId) => `/orders/${orderId}/link-invoice`,
  partnerJobPackSend: "/orders/partner-job-pack/send",
  partnerJobPackPreview: "/orders/partner-job-pack/preview",
  trackingLink: (orderId) => `/orders/${orderId}/tracking-link`,
};

/**
 * Get all orders with optional filters
 * @param {Object} filters - Filter options
 * @param {string} [filters.status] - Order status filter
 * @param {string} [filters.customerId] - Customer ID filter
 * @param {string} [filters.date] - Date filter
 * @param {number} [filters.limit] - Limit results
 * @param {number} [filters.offset] - Offset for pagination
 * @returns {Promise<Object>} API response
 */
export const fetchOrders = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.status) params.append("status", filters.status);
  if (filters.customerId) params.append("customerId", filters.customerId);
  if (filters.tripId) params.append("tripId", filters.tripId);
  if (filters.date) params.append("date", filters.date);
  if (filters.search) params.append("search", filters.search);
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.offset) params.append("offset", filters.offset.toString());

  const url = params.toString()
    ? `${ORDERS_ENDPOINTS.base}?${params}`
    : ORDERS_ENDPOINTS.base;
  const response = await api.get(url);
  return {
    success: true,
    data: response.data,
  };
};

/**
 * Active Deliveries operational summary (cards + tab counts)
 */
export const fetchOperationalSummary = async () => {
  const response = await api.get(ORDERS_ENDPOINTS.operationalSummary);
  return {
    success: true,
    data: response.data,
  };
};

/**
 * Get a single order by ID
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} API response
 */
export const fetchOrderById = async (orderId) => {
  const response = await api.get(ORDERS_ENDPOINTS.byId(orderId));
  
  // Backend returns: { success: true, data: { ...order, customerEmail } }
  // API client returns this directly, so response = { success: true, data: {...} }
  
  if (response?.success && response?.data) {
    // Extract the actual order object
    return {
      success: true,
      data: response.data, // This is the order object with customerEmail
    };
  }
  
  // Fallback for direct order object (backward compatibility)
  return {
    success: true,
    data: response,
  };
};

/**
 * Check completion eligibility for an order
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} API response
 */
export const fetchCompletionEligibility = async (orderId) => {
  const response = await api.get(ORDERS_ENDPOINTS.completionEligibility(orderId));
  return response;
};

/**
 * Create a new order
 * @param {Object} orderData - Order data
 * @returns {Promise<Object>} API response
 */
export const createOrder = async (orderData) => {
  const response = await api.post(ORDERS_ENDPOINTS.base, orderData);
  return response;
};

/**
 * Create order from accepted quote
 * @param {Object} orderData - Order data
 * @param {string} orderData.quoteId - Quote identifier
 * @param {string} [orderData.notes] - Additional notes
 * @returns {Promise<Object>} API response
 */
export const createOrderFromQuote = async (orderData) => {
  const response = await api.post(ORDERS_ENDPOINTS.createFromQuote, orderData);
  return response;
};

/**
 * Update an existing order
 * @param {string} orderId - Order identifier
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} API response
 */
export const updateOrder = async (orderId, updateData) => {
  const response = await api.put(ORDERS_ENDPOINTS.byId(orderId), updateData);
  return response;
};

/**
 * Delete an order
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} API response
 */
export const deleteOrder = async (orderId) => {
  const response = await api.delete(ORDERS_ENDPOINTS.byId(orderId));
  return response;
};

/**
 * Update order status
 * @param {string} orderId - Order identifier
 * @param {string} status - New status
 * @param {Object} [context] - Additional context (e.g., skipEmail, includeReviewRequest)
 * @returns {Promise<Object>} API response
 */
export const updateOrderStatus = async (orderId, status, context = {}) => {
  const response = await api.patch(ORDERS_ENDPOINTS.status(orderId), {
    status,
    ...context, // Include skipEmail, includeReviewRequest, and other context
  });
  return response;
};

/**
 * Assign vehicle and driver to order
 * @param {string} orderId - Order identifier
 * @param {string} vehicleId - Vehicle identifier
 * @param {string} driverId - Driver identifier
 * @returns {Promise<Object>} API response
 */
export const assignOrder = async (orderId, vehicleId, driverId) => {
  const response = await api.patch(ORDERS_ENDPOINTS.assign(orderId), {
    vehicleId,
    driverId,
  });
  return response;
};

/**
 * Get status templates (WhatsApp and Email) for an order status
 * @param {string} orderId - Order identifier
 * @param {string} status - Order status
 * @param {Object} [context] - Additional context data
 * @returns {Promise<Object>} API response with templates
 */
export const fetchStatusTemplates = async (orderId, status, context = {}) => {
  const params = new URLSearchParams();
  Object.keys(context).forEach((key) => {
    if (context[key] !== undefined && context[key] !== null) {
      params.append(key, context[key]);
    }
  });

  const url = params.toString()
    ? `${ORDERS_ENDPOINTS.statusTemplates(orderId, status)}?${params}`
    : ORDERS_ENDPOINTS.statusTemplates(orderId, status);
  const response = await api.get(url);
  return {
    success: true,
    data: response.data,
  };
};

/**
 * Get status communications tracking for an order
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} API response with communications
 */
export const fetchStatusCommunications = async (orderId) => {
  const response = await api.get(ORDERS_ENDPOINTS.statusCommunications(orderId));
  return {
    success: true,
    data: response.data,
  };
};

/**
 * Update status communication tracking
 * @param {string} orderId - Order identifier
 * @param {string} status - Order status
 * @param {Object} communicationData - Communication data
 * @param {boolean} [communicationData.whatsappSent] - WhatsApp sent
 * @param {boolean} [communicationData.emailSent] - Email sent
 * @returns {Promise<Object>} API response
 */
export const updateStatusCommunication = async (
  orderId,
  status,
  communicationData
) => {
  const response = await api.put(
    ORDERS_ENDPOINTS.statusCommunication(orderId, status),
    communicationData
  );
  return response;
};

/**
 * Send partner job pack (PDF with selected orders)
 * @param {Object} data - Job pack data
 * @param {string} data.partnerId - Partner ID
 * @param {Array<string>} data.orderIds - Array of order IDs to include
 * @returns {Promise<Object>} API response
 */
export const sendPartnerJobPack = async (data) => {
  const response = await api.post(ORDERS_ENDPOINTS.partnerJobPackSend, data);
  return response;
};

/**
 * Preview partner job pack PDF
 * @param {Object} data - Preview data
 * @param {Array<string>} data.orderIds - Array of order IDs to include
 * @returns {Promise<Object>} API response with PDF URL
 */
export const previewPartnerJobPack = async (data) => {
  const response = await api.post(ORDERS_ENDPOINTS.partnerJobPackPreview, data);
  return response;
};

/**
 * Get order participants (drivers, partners, suppliers, internal staff, contacts)
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} API response with participants data
 */
export const fetchOrderParticipants = async (orderId) => {
  const response = await api.get(ORDERS_ENDPOINTS.participants(orderId));
  return {
    success: true,
    data: response.data,
  };
};

/**
 * Issue or reuse a customer tracking link for an order
 * @param {string} orderId
 * @returns {Promise<Object>}
 */
export const fetchOrderTrackingLink = async (orderId) => {
  const response = await api.get(ORDERS_ENDPOINTS.trackingLink(orderId));
  return {
    success: true,
    data: response.data,
  };
};

/**
 * Invoices that may be linked to this order (Sprint 5.5)
 * @param {string} orderId
 * @param {{ q?: string, limit?: number, skip?: number }} params
 */
export const fetchInvoiceLinkCandidates = async (orderId, params = {}) => {
  const response = await api.get(ORDERS_ENDPOINTS.invoiceLinkCandidates(orderId), {
    params,
  });
  return response;
};

/**
 * Hard-link an invoice to this order’s job (Sprint 5.5)
 * @param {string} orderId
 * @param {{ invoiceId: string, allowCustomerMismatch?: boolean }} body
 */
export const postLinkInvoiceToOrder = async (orderId, body) => {
  const response = await api.post(ORDERS_ENDPOINTS.linkInvoice(orderId), body);
  return response;
};
